import React, { useState, useEffect } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker - use local worker file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const TermsModalPDFJS = ({ isOpen, toggle }) => {
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        return localStorage.getItem('Language') || 'EN';
    });
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);

    useEffect(() => {
        setSelectedLanguage(localStorage.getItem('Language') || 'EN');
    }, [isOpen]);

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    function changePage(offset) {
        setPageNumber(prevPageNumber => prevPageNumber + offset);
    }

    function previousPage() {
        changePage(-1);
    }

    function nextPage() {
        changePage(1);
    }

    function zoomIn() {
        setScale(prevScale => Math.min(prevScale + 0.2, 2.0));
    }

    function zoomOut() {
        setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="terms-modal-overlay" size="lg" centered>
            <ModalBody className="terms-modal-body" style={{ position: 'relative', padding: 0, maxHeight: '80vh' }}>
                
                {/* PDF Viewer with Custom Controls */}
                <div style={{ 
                    height: '80vh',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    
                    {/* Safari-style minimal controls - only visible on hover */}
                    <div 
                        className="pdf-controls"
                        style={{ 
                            position: 'absolute',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex', 
                            alignItems: 'center',
                            padding: '8px 16px',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            borderRadius: '20px',
                            zIndex: 100,
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: 'auto'
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = '1'}
                        onMouseLeave={(e) => e.target.style.opacity = '0'}
                    >
                        <button 
                            onClick={previousPage} 
                            disabled={pageNumber <= 1} 
                            style={{ 
                                background: 'transparent',
                                border: 'none',
                                color: pageNumber <= 1 ? '#666' : 'white',
                                cursor: pageNumber <= 1 ? 'default' : 'pointer',
                                fontSize: '14px',
                                padding: '4px 8px'
                            }}
                        >
                            ‹
                        </button>
                        <span style={{ 
                            color: 'white', 
                            fontSize: '12px', 
                            margin: '0 12px',
                            fontFamily: 'system-ui, -apple-system'
                        }}>
                            {pageNumber} / {numPages}
                        </span>
                        <button 
                            onClick={nextPage} 
                            disabled={pageNumber >= numPages}
                            style={{ 
                                background: 'transparent',
                                border: 'none',
                                color: pageNumber >= numPages ? '#666' : 'white',
                                cursor: pageNumber >= numPages ? 'default' : 'pointer',
                                fontSize: '14px',
                                padding: '4px 8px'
                            }}
                        >
                            ›
                        </button>
                    </div>

                    {/* PDF Document - Safari style clean presentation */}
                    <div 
                        style={{ 
                            flex: 1, 
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            backgroundColor: '#f5f5f5',
                            position: 'relative',
                            padding: '20px'
                        }}
                        onMouseEnter={() => {
                            const controls = document.querySelector('.pdf-controls');
                            if (controls) controls.style.opacity = '1';
                        }}
                        onMouseLeave={() => {
                            const controls = document.querySelector('.pdf-controls');
                            if (controls) controls.style.opacity = '0';
                        }}
                    >
                        <Document
                            file="/documents/FORUM 26 Business Center Conference Room-TC.pdf"
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(error) => {
                                console.error('PDF load error:', error);
                                console.error('PDF path attempted:', '/documents/FORUM 26 Business Center Conference Room-TC.pdf');
                            }}
                            loading={
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    height: '400px',
                                    color: '#666',
                                    fontFamily: 'system-ui, -apple-system'
                                }}>
                                    Loading PDF...
                                </div>
                            }
                            error={
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    height: '400px',
                                    color: '#999',
                                    fontFamily: 'system-ui, -apple-system'
                                }}>
                                    Failed to load PDF. Please try again.
                                </div>
                            }
                        >
                            <Page 
                                pageNumber={pageNumber} 
                                scale={scale}
                                renderTextLayer={true}
                                renderAnnotationLayer={false}
                                width={Math.min(window.innerWidth * 0.9, 600)}
                                loading={
                                    <div style={{ 
                                        width: Math.min(window.innerWidth * 0.9, 600) + 'px',
                                        height: '800px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#666'
                                    }}>
                                        Rendering page...
                                    </div>
                                }
                                error={
                                    <div style={{ 
                                        width: Math.min(window.innerWidth * 0.9, 600) + 'px',
                                        height: '800px',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#999'
                                    }}>
                                        Failed to render page
                                    </div>
                                }
                            />
                        </Document>
                    </div>
                    
                    {/* Close button overlaying the PDF */}
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={toggle} 
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            zIndex: 1000
                        }}
                    />
                </div>
            </ModalBody>
        </Modal>
    );
};

export default TermsModalPDFJS;